import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getContext } from '@adornis/baseql/server/context.js';
import { type AuthenticationContext, isAuthenticatedContext, isAdministrator } from '../server/auth.js';
import { getSonioxVoiceCatalog } from '../server/soniox-voice-catalog.js';
import { previewSonioxVoice } from '../server/soniox-voice-preview.js';
import {
  isVoiceSelectionEnabled,
  readVoiceSelectionSettings,
  saveVoiceSelectionSetting,
} from '../server/voice-selection-settings.js';

function voiceSelectionUser() {
  const context = getContext() as AuthenticationContext | undefined;
  if (!context || context.serverContext || typeof context.userID !== 'string' || !isAuthenticatedContext(context))
    throw new Error('VOICE_SELECTION_AUTH_REQUIRED');
  return context;
}
function requireEnabledVoiceSelection() {
  if (!isVoiceSelectionEnabled()) throw new Error('VOICE_SELECTION_DISABLED');
}
async function voiceSelectionStatus(context: AuthenticationContext) {
  const settings = await readVoiceSelectionSettings();
  return { ...settings, canSet: settings.enabled && isAdministrator(context) };
}
export const getVoiceSelectionSettings = registerQuery({
  type: () => String,
  operationName: 'getVoiceSelectionSettings',
  resolve: () => async () => JSON.stringify(await voiceSelectionStatus(voiceSelectionUser())),
  params: [],
});
export const getVoiceSelectionCatalog = registerQuery({
  type: () => String,
  operationName: 'getVoiceSelectionCatalog',
  resolve: () => async () => {
    const context = voiceSelectionUser();
    requireEnabledVoiceSelection();
    const [settings, catalog] = await Promise.all([voiceSelectionStatus(context), getSonioxVoiceCatalog()]);
    requireEnabledVoiceSelection();
    if (settings.model !== catalog.model || settings.region !== catalog.region)
      throw new Error('VOICE_SELECTION_CONFIGURATION_CHANGED');
    const priority = (id: string) => (id === settings.defaultVoice ? 0 : id === settings.currentVoice ? 1 : 2);
    const voices = [...catalog.voices].sort(
      (a, b) => priority(a.id) - priority(b.id) || a.name.localeCompare(b.name, 'en', { numeric: true }),
    );
    return JSON.stringify({ ...settings, ...catalog, voices });
  },
  params: [],
});
export const previewVoiceSelection = registerMutation({
  type: () => String,
  operationName: 'previewVoiceSelection',
  resolve: (voice: string) => async () => {
    const context = voiceSelectionUser();
    requireEnabledVoiceSelection();
    return JSON.stringify(await previewSonioxVoice(voice, context.userID as string));
  },
  params: [{ name: 'voice', type: () => String }],
});
export const saveVoiceSelection = registerMutation({
  type: () => String,
  operationName: 'saveVoiceSelection',
  resolve: (voice: string) => async () => {
    const context = voiceSelectionUser();
    requireEnabledVoiceSelection();
    if (!isAdministrator(context)) throw new Error('VOICE_SELECTION_ADMIN_REQUIRED');
    await saveVoiceSelectionSetting(voice, context.userID as string);
    return JSON.stringify(await voiceSelectionStatus(context));
  },
  params: [{ name: 'voice', type: () => String }],
});
